
import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { caseStudies } from '../../data/case-studies';

export default function CaseStudyDetail() {
    const { t } = useTranslation();
    const { slug } = useParams<{ slug: string }>();
    const study = caseStudies.find(s => s.slug === slug);

    useEffect(() => {
        if (study) {
            document.title = `${t(study.companyKey)} - Case Study | reputrust.ai`;
            window.scrollTo(0, 0);
        }
    }, [study, t]);

    if (!study) {
        return (
            <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A] flex flex-col">
                <Header />
                <div className="flex-grow flex items-center justify-center pt-20 pb-20 text-center relative overflow-hidden">
                    {/* Background Elements */}
                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-[100px] animate-pulse"></div>
                    </div>

                    <div className="bg-white p-12 rounded-2xl max-w-md mx-auto relative z-10 shadow-lg border border-slate-200">
                        <h1 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Inter Tight, sans-serif' }}>Case Study nicht gefunden</h1>
                        <p className="text-slate-600 mb-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>Die angeforderte Case Study konnte nicht gefunden werden.</p>
                        <Link to="/case-studies" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] border border-blue-200 text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] px-6 py-3 rounded-lg hover:bg-blue-100 transition-all duration-300 font-medium uppercase tracking-wider">
                            Zurück zur Übersicht
                            <i className="ri-arrow-left-line"></i>
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 text-slate-600 selection:bg-tron-primary/30 selection:text-[#0F172A]">
            <Header />

            <div className="pt-20 pb-12 relative">
                {/* Hero Section */}
                <div className="relative h-[400px] md:h-[500px] overflow-hidden group">
                    <img
                        src={study.image}
                        alt={t(study.companyKey)}
                        className="w-full h-full object-cover object-center scale-105 group-hover:scale-110 transition-transform duration-[2s]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-slate-50/90 to-transparent"></div>

                    {/* Animated Overlay */}
                    <div className="absolute inset-0 bg-[url('/images/grid-pattern.png')] opacity-5 mix-blend-overlay"></div>

                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 z-10">
                        <div className="max-w-4xl mx-auto">
                            <div className="flex flex-wrap items-center gap-4 mb-6">
                                <div className="bg-white/80 backdrop-blur-md p-2 rounded-lg border border-slate-200/50 shadow-sm">
                                    {/* Placeholder for Logo if actual image not available, or use text */}
                                    <img src={study.logo} alt="Logo" className="h-8 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = 'none' }} />
                                    <span className="text-[#0F172A] font-bold px-2" style={{ display: 'none' }}>{t(study.companyKey)}</span>
                                </div>
                                <div className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-600 px-3 py-1 rounded-full text-sm font-medium backdrop-blur-md shadow-sm">
                                    {t(study.industryKey)}
                                </div>
                            </div>

                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                                Erfolgsgeschichte: <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]">{t(study.companyKey)}</span>
                            </h1>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-slate-600 text-sm md:text-base font-orbitron tracking-wide border-t border-slate-200 pt-6 mt-6">
                                <div>
                                    <span className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Zeitraum</span>
                                    {t(study.durationKey)}
                                </div>
                                {study.results.map((res, idx) => (
                                    <div key={idx}>
                                        <span className="block text-slate-400 text-xs uppercase tracking-wider mb-1">{t(res.labelKey)}</span>
                                        {t(res.metricKey)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 relative z-10">
                    {/* Background Elements */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full overflow-hidden pointer-events-none -z-10">
                        <div className="absolute top-40 -left-20 w-96 h-96 bg-tron-primary/5 rounded-full blur-[100px]"></div>
                        <div className="absolute bottom-40 -right-20 w-96 h-96 bg-tron-secondary/5 rounded-full blur-[100px]"></div>
                    </div>

                    <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-slate-200">
                        {/* Quote */}
                        <blockquote className="bg-slate-50 border-l-4 border-tron-primary p-6 rounded-r-xl mb-12">
                            <p className="text-xl italic text-slate-700 mb-4 font-dm-sans">"{t(study.testimonialKey)}"</p>
                            <footer className="text-sm font-bold text-[#0F172A] not-italic">— {t(study.companyKey)}</footer>
                        </blockquote>

                        {/* HTML Content */}
                        <div
                            className="prose prose-slate prose-lg max-w-none
                   prose-headings:font-bold prose-headings:text-[#0F172A] prose-headings:font-inter-tight
                   prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-l-4 prose-h2:border-tron-primary prose-h2:pl-6
                   prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]
                   prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-6
                   prose-ul:my-6 prose-ul:space-y-3 prose-ul:pl-6
                   prose-li:text-slate-600 prose-li:marker:text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]
                   prose-strong:text-[#0F172A] prose-strong:font-bold"
                            style={{ fontFamily: 'DM Sans, sans-serif' }}
                            dangerouslySetInnerHTML={{ __html: study.content }}
                        />

                        {/* CTA Box */}
                        <div className="mt-16 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-2xl p-8 text-center relative overflow-hidden group shadow-sm">
                            <div className="absolute inset-0 bg-gradient-to-r from-[#10B981] to-[#1E3A8A]/50 group-hover:bg-gradient-to-r from-[#10B981] to-[#1E3A8A] transition-colors duration-500"></div>
                            <div className="relative z-10">
                                <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Möchten Sie ähnliche Ergebnisse erzielen?</h3>
                                <p className="text-slate-600 mb-8 max-w-2xl mx-auto">Starten Sie noch heute Ihre 7-tägige kostenlose Testphase und verbessern Sie Ihre Online-Reputation.</p>
                                <div className="flex justify-center gap-4">
                                    <a href="/#preise" className="bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white font-bold px-8 py-3 rounded-lg hover:bg-blue-900 transition-all shadow-md inline-block">
                                        Kostenlos testen
                                    </a>
                                    <Link to="/demo" className="bg-white text-slate-700 font-bold px-8 py-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-all">
                                        Demo buchen
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Back Link */}
                        <div className="mt-12 pt-8 border-t border-slate-200">
                            <Link
                                to="/case-studies"
                                className="inline-flex items-center gap-2 text-slate-500 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#10B981] hover:to-[#1E3A8A] transition-colors font-medium hover:translate-x-[-4px] transform duration-300"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Zurück zur Übersicht
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
